import React from "react";
import { Eye, Phone, MousePointerClick, Navigation, TrendingUp, TrendingDown, Minus } from "lucide-react";

const fmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000   ? `${(n / 1_000).toFixed(1)}k`
  : String(n);

const Delta: React.FC<{ current: number; previous?: number }> = ({ current, previous }) => {
  if (!previous || previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up  = pct > 0;
  const eq  = pct === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full
      ${eq ? "bg-slate-100 text-slate-400" : up ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"}`}>
      {eq ? <Minus size={9} /> : up ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {eq ? "=" : `${up ? "+" : ""}${pct}%`}
    </span>
  );
};

interface KpiCardProps {
  label: string;
  value: number;
  compareValue?: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  ringColor: string;
  compareEnabled: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({
  label, value, compareValue, icon, color, bgColor, ringColor, compareEnabled,
}) => (
  <div className={`flex items-center gap-4 p-4 rounded-2xl border ${ringColor} ${bgColor}
    transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 truncate">{label}</p>
      <div className="flex items-end gap-2">
        <p className="text-[22px] font-bold text-slate-800 leading-tight">{fmt(value)}</p>
        {compareEnabled && <Delta current={value} previous={compareValue} />}
      </div>
      {compareEnabled && compareValue !== undefined && (
        <p className="text-[10px] text-slate-400 mt-0.5">vs {fmt(compareValue)}</p>
      )}
    </div>
  </div>
);

interface Props {
  totalImpressions: number;
  callClicks: number;
  websiteClicks: number;
  directionRequests: number;
  compareEnabled?: boolean;
  compareTotalImpressions?: number;
  compareCallClicks?: number;
  compareWebsiteClicks?: number;
  compareDirectionRequests?: number;
}

const KpiGrid: React.FC<Props> = ({
  totalImpressions, callClicks, websiteClicks, directionRequests,
  compareEnabled = false,
  compareTotalImpressions, compareCallClicks, compareWebsiteClicks, compareDirectionRequests,
}) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    <KpiCard label="Impressions"    value={totalImpressions}  compareValue={compareTotalImpressions}  compareEnabled={compareEnabled} icon={<Eye size={18} />}               color="bg-blue-50 text-[#0F52BA]"      bgColor="bg-blue-50/30"   ringColor="border-blue-100" />
    <KpiCard label="Appels"         value={callClicks}         compareValue={compareCallClicks}         compareEnabled={compareEnabled} icon={<Phone size={18} />}             color="bg-emerald-50 text-emerald-600" bgColor="bg-emerald-50/30" ringColor="border-emerald-100" />
    <KpiCard label="Clics site web" value={websiteClicks}      compareValue={compareWebsiteClicks}      compareEnabled={compareEnabled} icon={<MousePointerClick size={18} />} color="bg-orange-50 text-orange-500"   bgColor="bg-orange-50/30"  ringColor="border-orange-100" />
    <KpiCard label="Itinéraires"    value={directionRequests}  compareValue={compareDirectionRequests}  compareEnabled={compareEnabled} icon={<Navigation size={18} />}        color="bg-purple-50 text-purple-500"   bgColor="bg-purple-50/30"  ringColor="border-purple-100" />
  </div>
);

export default KpiGrid;